/**
 * The visitor's country, as resolved by `/api/geo` (Cloudflare's `cf-ipcountry`,
 * with a Vercel fallback) and cached in the `client_country` cookie.
 *
 * The cookie exists because the app is a SPA (`ssr: false`): there is no server
 * render to inject the country, so region-dependent UI would otherwise have to
 * wait a round trip on every load. It is also forwarded to the edge functions as
 * `X-Client-Country` by `utils/$fetch.ts` — a display-only hint, which the
 * backend consults solely for payment-method visibility and never for access
 * control (see `_shared/security/geoBlocking.ts`).
 *
 * `refresh()` lets a caller re-resolve on demand. The boot-time plugin only runs
 * once per app start, so without it a VPN switched on mid-session is invisible
 * until a full reload.
 */

import type { Ref } from 'vue'

/** Cloudflare's "unknown"/Tor sentinels — never worth persisting over a real answer. */
const UNKNOWN_COUNTRIES = new Set(['XX', 'T1'])

/**
 * Deliberately far longer than any plausible session. Nuxt's `useCookie` only
 * touches `document.cookie` when the ref's value *changes*, so a stable country
 * is written once and its Max-Age is never re-armed. At 24h a long-lived tab
 * could outlive the cookie: `utils/$fetch.ts` reads `document.cookie` directly,
 * so `X-Client-Country` would silently stop being sent while the in-memory ref
 * still held the region — exactly the UI-offers/server-refuses split this
 * composable exists to close. A longer window is safe because the value is now
 * re-resolved on every boot *and* every modal open.
 */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7

// Shares the in-flight *request* between overlapping callers (plugin at boot,
// modal on open) — not the write. Each caller still updates its own cookie ref,
// because `useCookie` hands out a distinct ref per call and a promise that
// closed over someone else's ref would leave the joiner stale.
let inFlight: Promise<string | null> | null = null

/** Resolve the current country, or null if it could not be established. */
function resolveCountry(): Promise<string | null> {
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const { country } = await $fetch<{ country: string }>('/api/geo')
      const normalised = country?.toUpperCase()
      // An unknown answer is not evidence the visitor moved — keep what we have
      // rather than dropping them to the default region list on a blip. (A real
      // Tor/proxy visitor therefore keeps their previous region client-side while
      // the server resolves none; that is the deliberate trade.)
      if (!normalised || UNKNOWN_COUNTRIES.has(normalised)) return null
      return normalised
    } catch (err) {
      // Non-fatal and fully recovered — the cached region still stands, and this
      // now runs per modal open rather than once per boot.
      console.warn('[geo] failed to resolve client country:', err)
      return null
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

export interface UseClientCountry {
  /** ISO-2 country, or undefined until one has been resolved. */
  country: Ref<string | undefined>
  /** Re-resolve; resolves true when the country actually moved. */
  refresh: () => Promise<boolean>
}

export function useClientCountry(): UseClientCountry {
  const country = useCookie<string | undefined>('client_country', {
    path: '/',
    sameSite: 'lax',
    maxAge: COOKIE_MAX_AGE_SECONDS,
  })

  async function refresh(): Promise<boolean> {
    const resolved = await resolveCountry()
    if (!resolved || resolved === country.value) return false
    country.value = resolved
    return true
  }

  return { country, refresh }
}
